<template>
  <NotFound v-if="notFound" />
  <vl-region v-if="loading">
    <div v-vl-align:center>
      <vl-loader :message="$t('general.pageLoading')" />
    </div>
  </vl-region>
  <vl-region v-if="delivery && !loading">
    <vl-layout>
      <PbsGoBackLink />
      <vl-grid v-vl-spacer:bottom.medium>
        <vl-column width="9" width-xs="12">
          <vl-alert
            v-if="alert.visible"
            :icon="alert.icon"
            :mod-error="alert.isError"
            :mod-success="alert.isSuccess"
            closable
            :close-text="$t('delivery.detail.alert.closeText')"
            :title="alert.title"
            :content="alert.content"
            @close="closeAlert"
          />
        </vl-column>
        <vl-column width="9" width-xs="12">
          <vl-title tag-name="h1">{{ $t('delivery.detail.title') }}</vl-title>
        </vl-column>
        <vl-column width="3" width-xs="12">
          <vl-button v-if="delivery.canAbort" type="button" mod-secondary @click="showcancelDeliveryModal">{{
            $t('delivery.detail.cancelDelivery.label')
          }}</vl-button>
        </vl-column>
        <vl-column>
          <vl-properties>
            <vl-properties-column>
              <vl-properties-list>
                <vl-properties-label>{{ $t('delivery.detail.definition.label') }}</vl-properties-label>
                <vl-properties-data>
                  <PbsEntityRouterLink type="deliveryProcessDefinition" :detail="delivery.definition" target="_blank"
                /></vl-properties-data>
                <vl-properties-label>{{ $t('delivery.detail.status.label') }}</vl-properties-label>
                <vl-properties-data>{{ delivery.status }}</vl-properties-data>
                <vl-properties-label>{{ $t('delivery.detail.step.label') }}</vl-properties-label>
                <vl-properties-data>{{ delivery.statusId !== 100 ? delivery.step : "/" }}</vl-properties-data>
                <vl-properties-label v-if="delivery.statusId == 2 || delivery.statusId == 3">{{ $t('delivery.detail.reason.label') }}</vl-properties-label>
                <vl-properties-data v-if="delivery.statusId == 2 || delivery.statusId == 3">{{ getFirstErrorLine(delivery.failReason) }}</vl-properties-data>
                <vl-properties-label>{{ $t('delivery.detail.organisation.label') }}</vl-properties-label>
                <vl-properties-data>{{ delivery.organisation }}</vl-properties-data>
                <vl-properties-label>{{ $t('delivery.detail.createdBy.label') }}</vl-properties-label>
                <vl-properties-data>{{ delivery.createdBy }}</vl-properties-data>
                <vl-properties-label>{{ $t('delivery.detail.createdOn.label') }}</vl-properties-label>
                <vl-properties-data>{{ dateTimeFilter(delivery.createdOn) }}</vl-properties-data>
                <vl-properties-label>{{ $t('delivery.detail.isDryRun.label') }}</vl-properties-label>
                <vl-properties-data>{{ delivery.isDryRun ? $t('general.yes') : $t('general.no') }}</vl-properties-data>
              </vl-properties-list>
            </vl-properties-column>
            <vl-properties-column>
              <vl-properties-list>
                <vl-properties-label>{{ $t('delivery.detail.dataset.label') }}</vl-properties-label>
                <vl-properties-data>
                  <vl-button v-if="delivery.fileName" type="button" :mod-loading="loadingDownload" @click="download">{{
                    $t('delivery.detail.dataset.action.label')
                  }}</vl-button>
                </vl-properties-data>
                <vl-properties-label>{{ $t('delivery.detail.feedback.label') }}</vl-properties-label>
                <vl-properties-data>
                  <a
                    v-if="delivery.qualityReport"
                    :href="delivery.qualityReport"
                    class="vl-link"
                    target="_blank"
                    rel="noopener"
                  >
                    {{ $t('delivery.detail.feedback.action.label') }}
                  </a>
                </vl-properties-data>
              </vl-properties-list>
            </vl-properties-column>
          </vl-properties>
        </vl-column>
      </vl-grid>
      <vl-grid>
        <vl-column>
          <vl-properties>
            <vl-properties-column v-if="delivery.runSteps && delivery.runSteps.length" mod-full>
              <vl-title tag-name="h2">{{ $t('delivery.detail.runSteps.title') }}</vl-title>
              <vl-accordion-list v-if="delivery.runSteps && delivery.runSteps.length">
                <vl-accordion-list-item v-for="(runStep, i) in delivery.runSteps" :id="i" :key="i">
                  <vl-accordion :id="i.toString()" :title="getRunStepDescription(runStep)" :open="!!runStep.warnings || !!runStep.error || !runStep.end">
                    <vl-properties-list v-vl-spacer:bottom.small>
                      <vl-properties-label>{{ $t('delivery.detail.runSteps.start.label') }}</vl-properties-label>
                      <vl-properties-data>{{ dateTimeFilter(runStep.start) }}</vl-properties-data>
                      <vl-properties-label>{{ $t('delivery.detail.runSteps.end.label') }}</vl-properties-label>
                      <vl-properties-data>{{ dateTimeFilter(runStep.end) }}</vl-properties-data>
                      <vl-properties-label>{{ $t('delivery.detail.runSteps.warnings.label') }}</vl-properties-label>
                      <vl-properties-data>
                        <div style="white-space: pre-line;">{{ runStep.warnings }}</div>
                      </vl-properties-data>
                      <vl-properties-label>{{ $t('delivery.detail.runSteps.error.label') }}</vl-properties-label>
                      <vl-properties-data>{{ runStep.error }}</vl-properties-data>
                    </vl-properties-list>
                  </vl-accordion>
                </vl-accordion-list-item>
              </vl-accordion-list>
            </vl-properties-column>
          </vl-properties>
        </vl-column>
      </vl-grid>
      <vl-grid>
        <vl-column>
          <vl-properties>
            <vl-properties-column v-if="delivery.products && delivery.products.length" mod-full>
              <vl-title tag-name="h2">{{ $t('delivery.create.products.title') }}</vl-title>
              <vl-accordion-list v-if="delivery.products && delivery.products.length">
                <vl-accordion-list-item v-for="(product, i) in delivery.products" :id="i" :key="i">
                  <vl-accordion :id="i.toString()" :title="product.productSpecificationLabel" :open="true">
                    <vl-properties-list v-vl-spacer:bottom.small>
                      <vl-properties-label>{{ $t('delivery.detail.isFirstTime.label') }}</vl-properties-label>
                      <vl-properties-data>{{
                        product.isFirstTime ? $t('general.yes') : $t('general.no')
                      }}</vl-properties-data>
                    </vl-properties-list>
                    <vl-title tag-name="h3">{{ $t('delivery.detail.metadata.title') }}</vl-title>
                    <vl-annotation v-if="!(product.versionDate && product.versionDescription)">{{
                      $t('delivery.detail.metadata.publicationSkipped.label')
                    }}</vl-annotation>
                    <vl-properties-list v-if="product.versionDate && product.versionDescription">
                      <vl-properties-label>{{ $t('delivery.detail.metadata.versionDate.label') }}</vl-properties-label>
                      <vl-properties-data>{{ dateFilter(product.versionDate) }}</vl-properties-data>
                      <vl-properties-label>{{
                        $t('delivery.detail.metadata.temporalLimitation.label')
                      }}</vl-properties-label>
                      <vl-properties-data
                        >{{ dateFilter(product.temporalLimitationFrom) }} -
                        {{
                          dateFilter(product.temporalLimitationTo, $t('delivery.detail.noEndDate.label'))
                        }}</vl-properties-data
                      >
                      <vl-properties-label>{{
                        $t('delivery.detail.metadata.versionDescription.label')
                      }}</vl-properties-label>
                      <vl-properties-data>{{ product.versionDescription }}</vl-properties-data>
                    </vl-properties-list>
                  </vl-accordion>
                </vl-accordion-list-item>
              </vl-accordion-list>
            </vl-properties-column>
          </vl-properties>
        </vl-column>
      </vl-grid>
    </vl-layout>
  </vl-region>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, onUnmounted, reactive, toRefs } from 'vue';
import { DeliveryDetailService, DeliveryDetailServiceInstance } from './DeliveryDetail.service';
import { dateFilter, dateTimeFilter } from '@/filters';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import PbsEntityRouterLink from '@/components/PbsEntityRouterLink.vue';
import NotFound from '@/core/security/NotFound.vue';
import DeliveryCancelForm from './subcomponents/delivery-cancel-form/DeliveryCancelForm.vue';
import { useModalStore } from '@/modules/core/store/modal.store';
import PbsGoBackLink from '@/components/PbsGoBackLink.vue';
import { RunStep } from '../../api/clients';

export default defineComponent({
  components: {
    NotFound,
    PbsGoBackLink,
    PbsEntityRouterLink
  },
  setup() {
    const route = useRoute();
    const { t } = useI18n();
    const modalStore = useModalStore();
    const deliveryService: DeliveryDetailService = DeliveryDetailServiceInstance;

    // lifecycle hooks
    onMounted(async () => {
      await deliveryService.init(parseInt(route.params.id.toString(), 10));
      if (route.query.created === 'true') {
        alertSuccess(
          t('delivery.detail.created.alertSuccess.content').toString(),
          t('delivery.detail.created.alertSuccess.title').toString()
        );
      }
    });

    onUnmounted(() => {
      deliveryService.reset();
    });

    // reactive data
    const data = reactive({
      alert: {
        visible: false,
        isError: false,
        isSuccess: false,
        title: '',
        content: '',
        icon: ''
      },
      loadingDownload: false,
      loadingCancelDelivery: false
    });

    const id = computed((): number => {
      return parseInt(route.params.id as string, 10);
    });

    // computed values
    const delivery = computed(() => deliveryService.state.delivery);
    const notFound = computed(() => deliveryService.state.notFound);
    const loading = computed(() => deliveryService.state.loading);

    // methods
    const download = async () => {
      data.loadingDownload = true;
      try {
        await deliveryService.download();
      } catch (error) {
        alertError(
          t('delivery.detail.dataset.errorAlert.content').toString(),
          t('delivery.detail.dataset.errorAlert.title').toString()
        );
      } finally {
        data.loadingDownload = false;
      }
    };

    const showcancelDeliveryModal = () => {
      const modalContent = {
        title: t('delivery.detail.cancelDelivery.label'),
        component: DeliveryCancelForm,
        props: {
          id: id.value
        }
      };

      modalStore.showModal(modalContent);
    };

    const getRunStepDescription = (runStep: RunStep) => {
      let name = runStep.entityName ? `${runStep.name} ${runStep.entityName}` : runStep.name;

      if(runStep.retryCount != null && runStep.retryCount > 0){
        name = `${name}, herhaling ${runStep.retryCount}`
      }

      return name;
    };

    const getFirstErrorLine = (error: string) => {
      return error ? error.split('\n')[0] : '';
    };

    const alertError = (message: string, title: string) => {
      data.alert = {
        visible: true,
        isError: true,
        isSuccess: false,
        icon: 'alert-circle',
        title: title,
        content: message
      };
    };

    const alertSuccess = (message: string, title: string) => {
      data.alert = {
        visible: true,
        isError: false,
        isSuccess: true,
        icon: 'check',
        title: title,
        content: message
      };
    };

    const closeAlert = () => {
      data.alert = {
        visible: false,
        isError: false,
        isSuccess: false,
        icon: '',
        title: '',
        content: ''
      };
    };

    return {
      ...toRefs(data),
      dateTimeFilter,
      dateFilter,
      download,
      getRunStepDescription,
      getFirstErrorLine,
      showcancelDeliveryModal,
      closeAlert,
      delivery,
      notFound,
      loading
    };
  }
});
</script>